/**
 * Marketing site layout — LOTE M (2026-06-24).
 *
 * Web adaptation: structurally identical to the original Expo Router
 * version, only the platform primitives change (View→div, Text→span/p,
 * Pressable→button, expo-router→react-router-dom). All copy, colours,
 * fonts, spacings, routes and behaviour are preserved verbatim.
 *
 * Language detection order: ?lang= query → localStorage('mf_www_lang')
 * → navigator.language → 'es' (default).
 */
import { useEffect, useState, createContext, useContext, useMemo } from 'react';
import { Outlet, Link, useLocation, useSearchParams } from 'react-router-dom';
import { wwwTheme, wwwSpacing, wwwRadius, wwwFonts } from '@/src/website/theme';
import { WWW_T, WWW_LOCALES, WWW_LOCALE_LABEL, detectBrowserLang, type WwwLang, type WwwDict } from '@/src/website/i18n';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@/src/website/seo';

const LS_KEY = 'mf_www_lang';

interface Ctx { lang: WwwLang; setLang: (l: WwwLang) => void; t: WwwDict; }
const WwwCtx = createContext<Ctx | null>(null);
export const useWww = (): Ctx => {
  const v = useContext(WwwCtx);
  if (!v) throw new Error('useWww must be used under WwwLayout');
  return v;
};

// Web replacement for RN's useWindowDimensions(): subscribes to window resize
// and re-renders, keeping the exact same API used by the original code.
function useWindowDimensions() {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));
  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
}

export default function WwwLayout() {
  // Initial: ES until a useEffect resolves the real preference (SSR-safe).
  const [lang, setLangState] = useState<WwwLang>('es');
  const [searchParams] = useSearchParams();
  const paramLang = searchParams.get('lang');

  useEffect(() => {
    // 1) URL param wins (manual share / language switcher).
    if (paramLang && WWW_LOCALES.includes(paramLang as WwwLang)) {
      setLangState(paramLang as WwwLang);
      try { window.localStorage.setItem(LS_KEY, paramLang); } catch { /* ignore */ }
      return;
    }
    // 2) Persisted preference.
    try {
      const stored = window.localStorage.getItem(LS_KEY) as WwwLang | null;
      if (stored && WWW_LOCALES.includes(stored)) { setLangState(stored); return; }
    } catch { /* ignore */ }
    // 3) Browser locale.
    setLangState(detectBrowserLang());
  }, [paramLang]);

  const setLang = (l: WwwLang) => {
    setLangState(l);
    try { window.localStorage.setItem(LS_KEY, l); } catch { /* ignore */ }
  };

  // Keep <html lang> in sync with the active locale (SEO / screen readers).
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch { /* ignore */ }
  }, [lang]);

  const t = WWW_T[lang];
  const value = useMemo(() => ({ lang, setLang, t }), [lang, t]);

  return (
    <WwwCtx.Provider value={value}>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: wwwTheme.bg }}>
        <Navbar />
        <div style={{ flex: 1 }}>
          <Outlet />
        </div>
        <Footer />
      </div>
    </WwwCtx.Provider>
  );
}

// ============================== NAVBAR =====================================
function Navbar() {
  const { width } = useWindowDimensions();
  const isMobile = width < 760;
  const { t, lang, setLang } = useWww();
  const pathname = useLocation().pathname;
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const links: Array<{ label: string; href: string }> = [
    { label: t.navFeatures, href: '/features' },
    { label: t.navShop,     href: '/shop' },
    { label: t.navPricing,  href: '/pricing' },
    { label: t.navHelp,     href: '/help' },
    { label: t.navContact,  href: '/contact' },
  ];

  return (
    <div style={{ ...styles.nav, backgroundColor: wwwTheme.bg, borderBottomColor: wwwTheme.border }}>
      <div style={{ ...styles.navInner, paddingLeft: isMobile ? 20 : 48, paddingRight: isMobile ? 20 : 48 }}>
        <Link to={"/home"} style={{ textDecoration: 'none' }}>
          <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <img src="/logo-mark.png" alt="STILGIA" data-testid="navbar-logo-icon" style={{ width: 30, height: 30, display: 'block' }} />
            <span style={{ ...styles.logo, color: wwwTheme.text, fontFamily: wwwFonts.serifBold }}>STILGIA</span>
          </span>
        </Link>
        {!isMobile && (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 28 }}>
            {links.map(l => (
              <Link key={l.href} to={l.href} style={{ textDecoration: 'none' }}>
                <span style={{ color: pathname === l.href ? wwwTheme.text : wwwTheme.muted, fontSize: 14, fontWeight: 500 }}>{l.label}</span>
              </Link>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setLangOpen(o => !o)} style={{ ...styles.langPill, borderColor: wwwTheme.border, background: 'transparent', cursor: 'pointer' }}>
              <span style={{ color: wwwTheme.text, fontSize: 12, fontWeight: 600, letterSpacing: '0.5px' }}>{lang.toUpperCase()}</span>
            </button>
            {langOpen && (
              <div style={{ ...styles.langMenu, backgroundColor: wwwTheme.surface, borderColor: wwwTheme.border }}>
                {WWW_LOCALES.map(l => (
                  <button key={l} onClick={() => { setLang(l); setLangOpen(false); }} style={{ display: 'block', width: '100%', textAlign: 'left', paddingTop: 10, paddingBottom: 10, paddingLeft: 14, paddingRight: 14, background: 'transparent', border: 'none', cursor: 'pointer' }}>
                    <span style={{ color: l === lang ? wwwTheme.text : wwwTheme.muted, fontWeight: l === lang ? 600 : 400, fontSize: 13 }}>{WWW_LOCALE_LABEL[l]}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {isMobile && (
            <button onClick={() => setMenuOpen(o => !o)} style={{ ...styles.langPill, borderColor: wwwTheme.border, background: 'transparent', cursor: 'pointer' }}>
              <span style={{ color: wwwTheme.text, fontSize: 16 }}>{menuOpen ? '✕' : '☰'}</span>
            </button>
          )}
        </div>
      </div>
      {isMobile && menuOpen && (
        <div style={{ paddingLeft: 20, paddingRight: 20, paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {links.map(l => (
            <Link key={l.href} to={l.href} onClick={() => setMenuOpen(false)} style={{ paddingTop: 10, paddingBottom: 10, textDecoration: 'none' }}>
              <span style={{ color: wwwTheme.text, fontSize: 16, fontWeight: 500 }}>{l.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================== FOOTER =====================================
function Footer() {
  const { width } = useWindowDimensions();
  const isMobile = width < 760;
  const { t } = useWww();
  const year = new Date().getFullYear();
  return (
    <div style={{ ...styles.footer, borderTopColor: wwwTheme.border, paddingLeft: isMobile ? 20 : 48, paddingRight: isMobile ? 20 : 48 }}>
      <div style={{ ...styles.footerInner, display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 24 : 0 }}>
        <div style={{ maxWidth: 360 }}>
          <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 9 }}>
            <img src="/logo-mark.png" alt="STILGIA" data-testid="footer-logo-icon" style={{ width: 26, height: 26, display: 'block' }} />
            <span style={{ ...styles.logo, color: wwwTheme.text, fontFamily: wwwFonts.serifBold, fontSize: 22, display: 'block' }}>STILGIA</span>
          </span>
          <p style={{ color: wwwTheme.muted, fontSize: 13, marginTop: 8, marginBottom: 0, lineHeight: '19px' }}>{t.footerTagline}</p>
          <p style={{ color: wwwTheme.muted, fontSize: 12, marginTop: 16, marginBottom: 0 }}>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-appstore-link" style={{ color: wwwTheme.muted, textDecoration: 'underline' }}>{t.storeAppStoreSoon}</a>
            <span> · </span>
            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-googleplay-link" style={{ color: wwwTheme.muted, textDecoration: 'underline' }}>{t.storeGooglePlaySoon}</a>
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 28 }}>
          <Link to={"/privacy"} style={{ textDecoration: 'none' }}><span style={styles.footerLink}>{t.navPrivacy}</span></Link>
          <Link to={"/terms"} style={{ textDecoration: 'none' }}><span style={styles.footerLink}>{t.navTerms}</span></Link>
          <Link to={"/help"} style={{ textDecoration: 'none' }}><span style={styles.footerLink}>{t.navHelp}</span></Link>
          <Link to={"/contact"} style={{ textDecoration: 'none' }}><span style={styles.footerLink}>{t.navContact}</span></Link>
        </div>
      </div>
      <p style={{ color: wwwTheme.muted, fontSize: 11, marginTop: 28, marginBottom: 0 }}>{t.footerCopyright.replace('{year}', String(year))}</p>
    </div>
  );
}

// ============================== SHARED STYLES =================================
const styles: Record<string, React.CSSProperties> = {
  nav: { borderBottomWidth: 1, borderBottomStyle: 'solid' },
  navInner: { maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingTop: 18, paddingBottom: 18 },
  logo: { fontSize: 26, letterSpacing: '0.4px' },
  langPill: { borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.pill, paddingLeft: 12, paddingRight: 12, paddingTop: 6, paddingBottom: 6 },
  langMenu: { position: 'absolute', top: 38, right: 0, borderWidth: 1, borderStyle: 'solid', borderRadius: wwwRadius.md, minWidth: 140, boxShadow: '0 8px 24px rgba(0,0,0,0.32)', zIndex: 50 },
  cta: { borderRadius: wwwRadius.pill, paddingLeft: 18, paddingRight: 18, paddingTop: 10, paddingBottom: 10 },
  footer: { borderTopWidth: 1, borderTopStyle: 'solid', marginTop: wwwSpacing.xxl * 2, paddingTop: wwwSpacing.xl, paddingBottom: wwwSpacing.xl },
  footerInner: { maxWidth: 1180, width: '100%', marginLeft: 'auto', marginRight: 'auto', justifyContent: 'space-between' },
  footerLink: { color: wwwTheme.muted, fontSize: 13, fontWeight: 500 },
};
